import * as React from 'react'
import { useCallback, useMemo } from 'react'
import type { JSX } from 'react'

import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import {
  useAccountId,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { isOfType } from '~publish/helpers/typeGuards'
import type { CalendarPostCard_PostData } from '~publish/pages/Calendar/fragments/CalendarPostCard_Post'

import type { PostCard_PostData } from './PostCard'

// Metadata types derived from the `PostCard_Post` fragment selection, so they
// track exactly the fields the card requests rather than the full wire schema.
type PostCardMetadata = NonNullable<PostCard_PostData['metadata']>

type ThreadedMetadata = Extract<
  PostCardMetadata,
  {
    __typename:
      | 'TwitterPostMetadata'
      | 'ThreadsPostMetadata'
      | 'MastodonPostMetadata'
      | 'BlueskyPostMetadata'
  }
>

type PostThread = ThreadedMetadata['thread']

type Geolocation = NonNullable<
  Extract<
    PostCardMetadata,
    { __typename: 'InstagramPostMetadata' }
  >['geolocation']
>

// PostCardContext is a React Context that is used to pass data to the sub-components
// of PostCard. It is used to pass the post data to the PostCardHeader, PostCardFooter,
// and other sub-components. It is meant to be used exclusively within the PostCard component.
type PostCardContextValue = {
  // post object
  post: PostCard_PostData
}

export const PostCardContext = React.createContext<
  PostCardContextValue | undefined
>(undefined)

const PostCardContextProvider = ({
  post,
  children,
}: {
  post: PostCard_PostData
  children: React.ReactNode
}): JSX.Element => {
  const contextValue: PostCardContextValue = useMemo(() => ({ post }), [post])

  return (
    <PostCardContext.Provider value={contextValue}>
      {children}
    </PostCardContext.Provider>
  )
}

const usePostCardContext = (): PostCardContextValue => {
  const context = React.useContext(PostCardContext)

  if (context === undefined) {
    throw new Error(
      'usePostCardContext must be used within a PostCardContextProvider',
    )
  }

  return context
}

const usePostData = (): PostCard_PostData => {
  const { post } = usePostCardContext()

  return post
}

type CtaIdentifier = `${string}-${number}`

const useTrackActionClicked = (): ((cta: CtaIdentifier) => void) => {
  const { id, channel } = usePostData()
  const accountId = useAccountId()
  const organizationId = useOrganizationId()

  const trackPostAction = useCallback(
    (cta: CtaIdentifier): void => {
      if (!organizationId) return

      BufferTracker.postCardActionClicked({
        organizationId,
        action: cta,
        accountId: accountId || '',
        channelId: channel.id,
        channel: channel.service,
        postId: id,
        clientName: 'publishWeb',
      })
    },
    [accountId, channel.id, channel.service, id, organizationId],
  )

  return trackPostAction
}

const metadataSupportsThreads = (
  metadata: PostCard_PostData['metadata'],
): metadata is ThreadedMetadata => {
  return (
    !!metadata &&
    (isOfType(metadata, 'TwitterPostMetadata') ||
      isOfType(metadata, 'ThreadsPostMetadata') ||
      isOfType(metadata, 'MastodonPostMetadata') ||
      isOfType(metadata, 'BlueskyPostMetadata'))
  )
}

const useMetadataThreadCount = (
  metadata: PostCard_PostData['metadata'],
): number => {
  if (metadataSupportsThreads(metadata)) {
    return metadata.threadCount
  }
  // Returns one because the main post text is always the first thread
  return 1
}

const useMetadataThread = (
  metadata: PostCard_PostData['metadata'],
): PostThread | undefined => {
  if (metadataSupportsThreads(metadata)) {
    return metadata.thread
  }
}

const useMetadataFirstComment = (
  metadata: PostCard_PostData['metadata'],
): string | undefined => {
  if (
    isOfType(metadata, 'LinkedInPostMetadata') ||
    isOfType(metadata, 'FacebookPostMetadata') ||
    isOfType(metadata, 'InstagramPostMetadata')
  ) {
    return metadata.firstComment ?? ''
  }
}

const useMetadataGeolocation = (
  metadata: PostCard_PostData['metadata'],
): Geolocation | undefined => {
  if (isOfType(metadata, 'InstagramPostMetadata') && metadata.geolocation) {
    return metadata.geolocation
  }
  if (isOfType(metadata, 'ThreadsPostMetadata') && metadata.locationId) {
    return { id: metadata.locationId, text: metadata.locationName }
  }
}

const useMetadataTitle = (
  metadata:
    | PostCard_PostData['metadata']
    | CalendarPostCard_PostData['metadata'],
): string | undefined => {
  if (isOfType(metadata, 'GoogleBusinessPostMetadata') && metadata.title) {
    return metadata.title
  }
}

const useMetadataThreadsTopic = (
  metadata: PostCard_PostData['metadata'],
): string | undefined => {
  if (isOfType(metadata, 'ThreadsPostMetadata')) {
    return metadata.topic || undefined
  }
}

const useMetadataIsAiGenerated = (
  metadata: PostCard_PostData['metadata'],
): boolean => {
  if (
    isOfType(metadata, 'TiktokPostMetadata') ||
    isOfType(metadata, 'YoutubePostMetadata') ||
    isOfType(metadata, 'TwitterPostMetadata') ||
    isOfType(metadata, 'InstagramPostMetadata')
  ) {
    return metadata.isAiGenerated
  }
  return false
}

export {
  PostCardContextProvider,
  PostCardContextValue,
  useMetadataFirstComment,
  useMetadataGeolocation,
  useMetadataIsAiGenerated,
  useMetadataThread,
  useMetadataThreadCount,
  useMetadataThreadsTopic,
  useMetadataTitle,
  usePostCardContext,
  usePostData,
  useTrackActionClicked,
}
